import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, TrendingUp, MapPin, Scale, Ruler, GitCompare, ArrowRight, Snowflake, Wind, Gauge, Clock, Award, Headphones } from 'lucide-react';

const TRUST = [
  { icon: Scale, title: 'Highest capacity per footprint', copy: 'More output in less floor space.' },
  { icon: ShieldCheck, title: 'Built for business & science', copy: 'Repeatable, documentable cycles.' },
  { icon: TrendingUp, title: 'ROI in 12 to 18 months', copy: 'Premium margins, 25-yr shelf life.' },
  { icon: MapPin, title: 'Proudly built in Canada', copy: 'Premium parts, real support.' },
];

export const LandingTrustStrip = () => (
  <section className="py-12 section-padding bg-white border-y border-slate-200" data-testid="landing-trust-strip">
    <div className="max-w-7xl mx-auto grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {TRUST.map((t, i) => (
        <div key={i} className="flex items-start gap-3">
          <span className="w-11 h-11 rounded-md bg-[#0F172A] flex items-center justify-center flex-shrink-0"><t.icon className="h-5 w-5 text-[#CE8946]" /></span>
          <div>
            <h3 className="font-semibold text-[#0a0203] text-sm leading-tight">{t.title}</h3>
            <p className="text-sm text-gray-500 mt-1 leading-snug">{t.copy}</p>
          </div>
        </div>
      ))}
    </div>
  </section>
);

const FEATURES = [
  { icon: Gauge, title: 'Production-grade capacity', copy: 'Sized for real output, from pilot batches to high-volume runs.' },
  { icon: Wind, title: 'Gentle low-temp drying', copy: 'Sublimation preserves colour, structure and nutrition with no heat damage.' },
  { icon: Clock, title: 'Fully automated cycles', copy: 'Load, start, walk away, repeatable results day and night.' },
  { icon: Snowflake, title: '25-year shelf life', copy: 'Seal with oxygen absorbers for shelf-stable product that lasts.' },
  { icon: Award, title: 'Built to North American codes', copy: 'Premium components and a limited warranty on every unit.' },
  { icon: Headphones, title: 'Real support & service', copy: 'Talk to our team and access rebuild/repair through our partners.' },
];

export const LandingFeatureGrid = ({ eyebrow = 'Why D-Freeze', title = 'Engineered to grow your operation' }) => (
  <section className="py-16 section-padding bg-[#F8FAFC]" data-testid="landing-feature-grid">
    <div className="max-w-7xl mx-auto">
      <div className="mb-10 text-center">
        <span className="block text-[#CE8946] font-semibold text-sm uppercase tracking-[0.2em]">{eyebrow}</span>
        <h2 className="text-3xl font-bold text-[#0a0203] mt-2">{title}</h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {FEATURES.map((f, i) => (
          <div key={i} className="bg-white rounded-lg p-6 border border-slate-200 hover:shadow-md transition-shadow">
            <span className="w-12 h-12 rounded-md bg-[#0F172A] flex items-center justify-center mb-4"><f.icon className="h-6 w-6 text-[#CE8946]" /></span>
            <h3 className="font-semibold text-[#0a0203] mb-2">{f.title}</h3>
            <p className="text-sm text-gray-500 leading-relaxed">{f.copy}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const LandingCTABand = ({ title = 'Not sure which model fits your operation?', copy = 'Size a unit to your target output in under a minute, or talk to our team.' }) => (
  <section className="py-16 section-padding bg-[#F8FAFC]" data-testid="landing-cta-band">
    <div className="max-w-5xl mx-auto bg-[#0F172A] rounded-2xl p-8 lg:p-12 text-center">
      <span className="block text-[#CE8946] font-semibold text-sm uppercase tracking-[0.2em]">Get started</span>
      <h2 className="text-3xl sm:text-4xl font-bold text-white mt-3 max-w-2xl mx-auto">{title}</h2>
      <p className="text-white/70 mt-4 max-w-xl mx-auto">{copy}</p>
      <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
        <Link to="/capacity-calculator" className="inline-flex items-center gap-2 bg-[#CE8946] text-[#0a0203] font-semibold px-6 py-3 rounded-md hover:bg-[#00a9b1] transition-colors" data-testid="landing-cta-calc">
          <Ruler className="h-4 w-4" /> Am I ready to scale?
        </Link>
        <Link to="/compare" className="inline-flex items-center gap-2 border border-white/30 text-white font-semibold px-6 py-3 rounded-md hover:bg-white/10 transition-colors" data-testid="landing-cta-compare">
          <GitCompare className="h-4 w-4" /> Compare models
        </Link>
        <Link to="/contact" className="inline-flex items-center gap-2 text-white/80 font-semibold px-4 py-3 rounded-md hover:text-white transition-colors">
          Free consultation <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  </section>
);
